import React, { useState, useEffect } from 'react';
import { X, CheckCircle, AlertTriangle } from 'lucide-react';

const Anamnese = ({ isOpen, onClose, preselectedService = null }) => {
  
  // Map service IDs to select values
  const getServiceValue = (serviceId) => {
    if (serviceId === 1) return 'detox';
    if (serviceId === 2) return 'articulation';
    if (serviceId === 3) return 'massage';
    return '';
  };
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    surgery: false,
    anticoagulants: false,
    pregnant: false,
    infection: false,
    autoimmune: false,
    noProblem: false,
    otherCondition: '',
  });

  const [submitted, setSubmitted] = useState(false);
  const [isEligible, setIsEligible] = useState(true);

  // Set preselected service when modal opens
  useEffect(() => {
    if (isOpen && preselectedService) {
      setFormData(prev => ({
        ...prev,
        service: getServiceValue(preselectedService)
      }));
    }
  }, [isOpen, preselectedService]);

  const handleCheckboxChange = (field) => {
    // Si "noProblem" est coché, décocher tous les autres
    if (field === 'noProblem' && !formData.noProblem) {
      setFormData({ 
        ...formData, 
        noProblem: true,
        surgery: false,
        anticoagulants: false,
        pregnant: false,
        infection: false,
        autoimmune: false
      });
    } else if (field === 'noProblem' && formData.noProblem) {
      setFormData({ ...formData, noProblem: false });
    } else {
      // Si un autre checkbox est coché, décocher "noProblem"
      setFormData({ 
        ...formData, 
        [field]: !formData[field],
        noProblem: false
      });
    }
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Check if any red flags are checked
    const hasRedFlags = formData.surgery || formData.anticoagulants || 
                       formData.pregnant || formData.infection || 
                       formData.autoimmune;
    
    setIsEligible(!hasRedFlags);
    setSubmitted(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      surgery: false,
      anticoagulants: false,
      pregnant: false,
      infection: false,
      autoimmune: false,
      otherCondition: '',
    });
    setSubmitted(false);
    setIsEligible(true);
  };

  if (!isOpen) return null;

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <div className="anamnese-overlay">
      <div className="anamnese-modal">
        <button className="anamnese-close" onClick={handleClose}>
          <X size={24} />
        </button>

        {!submitted ? (
          <>
            <h2 className="heading-1 anamnese-title">Questionnaire Médical</h2>
            <p className="body-regular anamnese-subtitle">
              Pour votre sécurité, veuillez répondre à ces questions avant votre séance.
            </p>

            <form onSubmit={handleSubmit} className="anamnese-form">
              <div className="form-group">
                <label className="body-regular">Nom complet *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="form-input"
                  placeholder="Votre nom"
                />
              </div>

              <div className="form-group">
                <label className="body-regular">Email *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="form-input"
                  placeholder="votre@email.com"
                />
              </div>

              <div className="form-group">
                <label className="body-regular">Téléphone *</label>
                <input
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="form-input"
                  placeholder="+32 XXX XX XX XX"
                />
              </div>

              <div className="form-group">
                <label className="body-regular">Prestation souhaitée *</label>
                <select
                  required
                  value={formData.service}
                  onChange={(e) => handleInputChange('service', e.target.value)}
                  className="form-input"
                >
                  <option value="">Sélectionnez une prestation</option>
                  <option value="detox">Détox Générale (Hijama) - 70€</option>
                  <option value="articulation">Hijama Ciblées pour une articulation - 80€</option>
                  <option value="massage">Massage Sportif - 50€</option>
                </select>
              </div>

              <div className="anamnese-questions">
                <h3 className="heading-3">Questions de Sécurité</h3>
                <p className="body-small anamnese-instructions">
                  Cochez les cases correspondant à votre situation actuelle :
                </p>

                <div className="checkbox-group">
                  <label className="checkbox-label no-problem-label">
                    <input
                      type="checkbox"
                      checked={formData.noProblem}
                      onChange={() => handleCheckboxChange('noProblem')}
                    />
                    <span className="body-regular">
                      ✅ Aucun problème de santé particulier
                    </span>
                  </label>

                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.surgery}
                      onChange={() => handleCheckboxChange('surgery')}
                    />
                    <span className="body-regular">
                      Avez-vous subi une opération chirurgicale récente (moins de 3 mois) ?
                    </span>
                  </label>

                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.anticoagulants}
                      onChange={() => handleCheckboxChange('anticoagulants')}
                    />
                    <span className="body-regular">
                      Prenez-vous un traitement anticoagulant pour la circulation sanguine ?
                    </span>
                  </label>

                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.pregnant}
                      onChange={() => handleCheckboxChange('pregnant')}
                    />
                    <span className="body-regular">
                      Êtes-vous enceinte ou allaitez-vous ?
                    </span>
                  </label>

                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.infection}
                      onChange={() => handleCheckboxChange('infection')}
                    />
                    <span className="body-regular">
                      Avez-vous de la fièvre, une infection ou une inflammation active ?
                    </span>
                  </label>

                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.autoimmune}
                      onChange={() => handleCheckboxChange('autoimmune')}
                    />
                    <span className="body-regular">
                      Avez-vous des maladies auto-immunes ou chroniques (lupus, sclérose, cancer, VIH, etc.) ?
                    </span>
                  </label>
                </div>

                <div className="form-group">
                  <label className="body-regular">
                    Autre problème de santé ou condition particulière à signaler :
                  </label>
                  <textarea
                    value={formData.otherCondition}
                    onChange={(e) => handleInputChange('otherCondition', e.target.value)}
                    className="form-textarea"
                    placeholder="Décrivez ici tout autre problème de santé pertinent..."
                    rows="4"
                  />
                </div>
              </div>

              <button type="submit" className="btn-primary anamnese-submit">
                Soumettre le questionnaire
              </button>
            </form>
          </>
        ) : (
          <div className="anamnese-result">
            {isEligible ? (
              <>
                <CheckCircle size={64} className="result-icon success" />
                <h2 className="heading-1">Questionnaire Validé ✅</h2>
                <p className="body-large result-message">
                  Merci {formData.name} ! D'après vos réponses, vous pouvez bénéficier de nos soins en toute sécurité.
                </p>
                <div className="service-selected">
                  <p className="body-regular">
                    <strong>Prestation sélectionnée :</strong> {
                      formData.service === 'detox' ? 'Détox Générale (Hijama) - 70€' :
                      formData.service === 'articulation' ? 'Hijama Ciblées pour une articulation - 80€' :
                      formData.service === 'massage' ? 'Massage Sportif - 50€' : ''
                    }
                  </p>
                </div>
                <p className="body-regular">
                  Nous vous contacterons au <strong>{formData.phone}</strong> pour confirmer votre rendez-vous.
                </p>
                <div className="result-actions">
                  <a 
                    href="https://calendly.com/h-gavci1907/30min" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn-primary"
                  >
                    Prendre Rendez-vous Maintenant
                  </a>
                  <button onClick={handleClose} className="btn-secondary">
                    Fermer
                  </button>
                </div>
              </>
            ) : (
              <>
                <AlertTriangle size={64} className="result-icon warning" />
                <h2 className="heading-1">Consultation Nécessaire</h2>
                <p className="body-large result-message">
                  Merci {formData.name} pour vos réponses. Pour votre sécurité, une consultation préalable est nécessaire avant de procéder aux soins.
                </p>
                <div className="service-selected">
                  <p className="body-regular">
                    <strong>Prestation souhaitée :</strong> {
                      formData.service === 'detox' ? 'Détox Générale (Hijama) - 70€' :
                      formData.service === 'articulation' ? 'Hijama Ciblées pour une articulation - 80€' :
                      formData.service === 'massage' ? 'Massage Sportif - 50€' : ''
                    }
                  </p>
                </div>
                <p className="body-regular">
                  Nous vous contacterons au <strong>{formData.phone}</strong> pour discuter de votre situation et vous proposer la meilleure approche thérapeutique.
                </p>
                <div className="result-actions">
                  <a 
                    href={`tel:+32489953024`}
                    className="btn-primary"
                  >
                    Nous Appeler Maintenant
                  </a>
                  <button onClick={handleClose} className="btn-secondary">
                    Fermer
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Anamnese;
